// =============================================================================
// Liabilities page — Phase 60B.
//
// Canonical home for non-credit-card debt: home/car/personal/education
// loans and other debt. Credit cards remain in `accounts` (per Phase 60
// decision #1) and are shown here as a filtered read-only summary.
//
// Backed by the new `liabilities` table with graceful fallback to the
// legacy `holdings` table where kind='liability'.
// =============================================================================
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CurrencyInput } from '@/components/ui/CurrencyInput';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { formatMoney, currencySymbol, CURRENCIES } from '@/lib/format';
import { Plus, Trash2, Home, Car, User as UserIcon, GraduationCap, FileWarning, CreditCard, Loader2, Zap } from 'lucide-react';
import DebtFocusTable from '@/components/liabilities/DebtFocusTable';
import usePageTitle from '@/hooks/usePageTitle';
import useFocusRefetch from '@/hooks/useFocusRefetch';
import { readCache, writeCache, withTimeout } from '@/lib/pageCache';

const KINDS = {
  home_loan:      { label: 'Home Loan',      icon: Home,           color: 'text-rose-600 dark:text-rose-400',        placeholder: 'Home loan / mortgage @ 8.4%' },
  car_loan:       { label: 'Car Loan',       icon: Car,            color: 'text-amber-600 dark:text-amber-400',      placeholder: 'Car loan @ 9.1%' },
  personal_loan:  { label: 'Personal Loan',  icon: UserIcon,       color: 'text-orange-600 dark:text-orange-400',    placeholder: 'Personal loan @ 13%' },
  education_loan: { label: 'Education Loan', icon: GraduationCap,  color: 'text-sky-600 dark:text-sky-400',          placeholder: 'Student / education loan @ 10%' },
  bnpl:           { label: 'BNPL',           icon: Zap,            color: 'text-fuchsia-600 dark:text-fuchsia-400',  placeholder: 'Tabby / Tamara / Postpay @ 0-30%' },
  other_debt:     { label: 'Other Debt',     icon: FileWarning,    color: 'text-muted-foreground',                    placeholder: 'Borrowed from family, …' },
};
const ORDER = ['home_loan', 'car_loan', 'personal_loan', 'education_loan', 'bnpl', 'other_debt'];

// v3.7.5 Phase 84.29 — auto-detect BNPL kind from name. Mirrors the
// heuristic in components/accounts/LiabilityForm.jsx so the two entry
// points behave consistently.
const BNPL_NAME_HINTS = /\b(tabby|tamara|postpay|klarna|afterpay|affirm|zip pay|zippay|clearpay|sezzle|spotii|cashew|split)\b/i;

export default function Liabilities() {
  usePageTitle('Liabilities');
  const { user, profile } = useAuth();
  const uid = user?.id || null;
  const currency = profile?.currency || 'INR';
  const cached = uid ? readCache(uid, 'liabilities:v1') : null;
  const [items, setItems] = useState(cached?.items || []);
  const [creditCards, setCreditCards] = useState(cached?.cards || []);
  const [loading, setLoading] = useState(!cached);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // Phase 60C.2 — when set, the dialog operates in EDIT mode on this liability.
  const [editingId, setEditingId] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [form, setForm] = useState({
    kind: 'home_loan', name: '', outstanding: '', principal_amount: '', currency, emi_amount: '', interest_rate: '', notes: '',
  });

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      // Liabilities
      let lres = await withTimeout(
        supabase.from('liabilities').select('*').order('updated_at', { ascending: false }),
        12000, 'liabilities.list',
      );
      let libs = [];
      if (lres?.error && /relation .*liabilities.* does not exist|liabilities.* schema cache/i.test(lres.error.message || '')) {
        console.warn('liabilities table missing — falling back to legacy holdings read. Apply schema_phase60b_wealth_liabilities.sql to enable the new tables.');
        const fb = await supabase.from('holdings').select('*').eq('kind', 'liability').order('updated_at', { ascending: false });
        libs = (fb?.data || []).map((h) => ({
          id: h.id, user_id: h.user_id, name: h.name || h.category, outstanding: h.value, currency: h.currency, notes: h.notes,
          kind: /home|mortgage/i.test(h.category || h.name || '') ? 'home_loan'
            : /car|auto/i.test(h.category || h.name || '') ? 'car_loan'
            : /personal/i.test(h.category || h.name || '') ? 'personal_loan'
            : /education|student/i.test(h.category || h.name || '') ? 'education_loan'
            : 'other_debt',
        }));
      } else if (lres?.error) {
        toast.error(lres.error.message);
      } else {
        libs = lres?.data || [];
      }
      setItems(libs);

      // Credit cards (from accounts, filtered)
      const cres = await supabase.from('accounts').select('*').eq('type', 'credit_card').order('name');
      const cards = cres?.data || [];
      setCreditCards(cards);

      if (uid) writeCache(uid, 'liabilities:v1', { items: libs, cards });
    } catch (e) {
      console.warn('Liabilities load failed:', e?.message || e);
    } finally { if (!silent) setLoading(false); }
  };
  useEffect(() => { if (uid) load(); /* eslint-disable-next-line */ }, [uid]);
  useFocusRefetch(() => load({ silent: true }), { minHiddenMs: 60_000 });

  const grouped = useMemo(() => {
    const out = { home_loan: [], car_loan: [], personal_loan: [], education_loan: [], bnpl: [], other_debt: [] };
    items.forEach((i) => { (out[i.kind] || out.other_debt).push(i); });
    return out;
  }, [items]);
  const totals = useMemo(() => {
    const out = { home_loan: 0, car_loan: 0, personal_loan: 0, education_loan: 0, bnpl: 0, other_debt: 0, all: 0, credit_cards: 0 };
    items.forEach((i) => {
      const v = Number(i.outstanding || 0);
      out[i.kind] = (out[i.kind] || 0) + v;
      out.all += v;
    });
    // Credit cards: outstanding = positive balance (negative balance means overpayment/credit, not debt)
    creditCards.forEach((c) => {
      const b = Number(c.balance || 0);
      if (b > 0) { out.credit_cards += b; out.all += b; }
    });
    return out;
  }, [items, creditCards]);

  const save = async (e) => {
    e?.preventDefault?.();
    if (saving) return;
    if (!form.name.trim() || !form.outstanding || Number(form.outstanding) <= 0) {
      toast.error('A name and outstanding balance help us help you.');
      return;
    }
    setSaving(true);
    try {
      const row = {
        user_id: user.id,
        kind: form.kind,
        name: form.name.trim(),
        outstanding: Number(form.outstanding),
        // Phase 72.0.1 — Starting / original principal. When the user
        // provides it we can compute a real "Paid down %" on the Debt
        // Freedom goal card (previously the card had to assume
        // principal == outstanding, which always rendered as 0% paid).
        principal_amount: form.principal_amount ? Number(form.principal_amount) : null,
        currency: form.currency || currency,
        emi_amount: form.emi_amount ? Number(form.emi_amount) : null,
        interest_rate: form.interest_rate ? Number(form.interest_rate) : null,
        notes: form.notes || null,
        source: 'manual',
      };
      if (editingId) {
        // EDIT — strip user_id + source to avoid trying to change them on update.
        const { user_id: _omitU, source: _omitS, ...patch } = row;
        const { error } = await supabase
          .from('liabilities')
          .update(patch)
          .eq('id', editingId);
        if (error) throw error;
        toast.success('Saved.');
      } else {
        const { error } = await supabase.from('liabilities').insert(row);
        if (error) throw error;
        toast.success(`${KINDS[form.kind].label} added.`);
      }
      setOpen(false);
      setEditingId(null);
      load();
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Could not save.');
    } finally { setSaving(false); }
  };

  // Pre-fills the form from an existing liability and switches the dialog
  // into EDIT mode. Triggered by clicking a row in the list.
  const openForEdit = (l) => {
    setEditingId(l.id);
    setForm({
      kind: l.kind || 'home_loan',
      name: l.name || '',
      outstanding: l.outstanding != null ? String(l.outstanding) : '',
      // Phase 72.0.1
      principal_amount: l.principal_amount != null ? String(l.principal_amount) : '',
      currency: l.currency || currency,
      emi_amount: l.emi_amount != null ? String(l.emi_amount) : '',
      interest_rate: l.interest_rate != null ? String(l.interest_rate) : '',
      notes: l.notes || '',
    });
    setOpen(true);
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this liability?')) return;
    const { error } = await supabase.from('liabilities').delete().eq('id', id);
    if (error) return toast.error(error.message);
    toast.success('Removed.');
    load();
  };

  const renderRow = (i) => {
    const meta = KINDS[i.kind] || KINDS.other_debt; const Icon = meta.icon;
    return (
      <Card
        key={i.id}
        className="rounded-2xl border-border/70 card-lift cursor-pointer hover:border-primary/40 focus-within:ring-2 focus-within:ring-primary/30 transition-colors"
        data-testid={`liab-row-${i.id.slice(0,8)}`}
        onClick={() => openForEdit(i)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openForEdit(i); } }}
        aria-label={`Edit ${i.name}`}
      >
        <CardContent className="p-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`h-10 w-10 rounded-xl grid place-items-center bg-muted/50 ${meta.color}`}><Icon className="h-4 w-4" /></div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{i.name}</div>
              <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap mt-0.5">
                <Badge variant="outline" className="rounded-full text-[10px]">{meta.label}</Badge>
                {i.emi_amount && <Badge variant="secondary" className="rounded-full text-[10px]">{formatMoney(Number(i.emi_amount), i.currency || currency, { compact: true })}/mo</Badge>}
                {i.interest_rate && <Badge variant="secondary" className="rounded-full text-[10px]">{i.interest_rate}%</Badge>}
                {i.currency && i.currency.toUpperCase() !== (currency || 'INR').toUpperCase() && (
                  <Badge variant="secondary" className="rounded-full text-[10px]">{i.currency}</Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <div className="text-sm font-medium tabular-nums text-destructive">−{formatMoney(Number(i.outstanding || 0), i.currency || currency, { compact: true })}</div>
            <Button
              variant="ghost" size="icon" className="rounded-xl"
              onClick={(e) => { e.stopPropagation(); remove(i.id); }}
              aria-label="Remove"
              data-testid={`liab-row-delete-${i.id.slice(0,8)}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderTile = (kindKey) => {
    const isAll = kindKey === 'all';
    const meta = isAll
      ? { label: 'Total Debt', icon: FileWarning, color: 'text-destructive' }
      : kindKey === 'credit_cards' ? { label: 'Credit Cards', icon: CreditCard, color: 'text-foreground' } : KINDS[kindKey];
    const Icon = meta.icon;
    return (
      <Card key={kindKey} className={`rounded-2xl border-border/70 ${isAll ? 'bg-destructive/5 border-destructive/30' : ''}`} data-testid={`liab-tile-${kindKey}`}>
        <CardContent className="p-4">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">
            <Icon className={`h-3.5 w-3.5 ${meta.color}`} /><span>{meta.label}</span>
          </div>
          <div className={`mt-1 tabular-nums font-display font-semibold ${isAll ? 'text-2xl text-destructive' : 'text-lg'}`}>
            {(totals[kindKey] || 0) > 0 ? '−' : ''}{formatMoney(totals[kindKey] || 0, currency, { compact: true })}
          </div>
        </CardContent>
      </Card>
    );
  };

  const visibleItems = activeTab === 'all' ? items
    : activeTab === 'credit_cards' ? [] : grouped[activeTab] || [];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold">Liabilities</h1>
          <p className="text-sm text-muted-foreground">Loans, credit card balances, and other debts you owe.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditingId(null); }}>
          <DialogTrigger asChild>
            <Button className="rounded-xl" onClick={() => { setEditingId(null); setForm({ ...form, kind: activeTab !== 'all' && activeTab !== 'credit_cards' ? activeTab : 'home_loan' }); }} data-testid="liab-add-button">
              <Plus className="h-4 w-4 mr-1" /> Add liability
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-display">
                {editingId ? 'Edit liability' : 'Add a liability'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={save} className="space-y-3">
              <div className="space-y-1.5">
                <Label>Type</Label>
                <div className="grid grid-cols-3 gap-2">
                  {ORDER.map((k) => {
                    const meta = KINDS[k]; const Icon = meta.icon; const sel = form.kind === k;
                    return (
                      <button key={k} type="button" onClick={() => setForm({ ...form, kind: k })}
                        className={`rounded-xl border px-3 py-2 text-left transition ${sel ? 'border-primary bg-primary/10 text-foreground' : 'border-border/60 hover:bg-muted/50 text-muted-foreground'}`}
                        data-testid={`liab-kind-${k}`}>
                        <Icon className={`h-4 w-4 mb-1 ${sel ? meta.color : ''}`} />
                        <div className="text-[11px] font-medium leading-tight">{meta.label}</div>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-muted-foreground">Credit cards live in Accounts — they show up here as a read-only summary.</p>
              </div>
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input value={form.name} onChange={(e) => {
                  const nextName = e.target.value;
                  const suggested = BNPL_NAME_HINTS.test(nextName) ? 'bnpl' : null;
                  if (suggested && form.kind === 'home_loan') {
                    setForm({ ...form, name: nextName, kind: suggested });
                  } else {
                    setForm({ ...form, name: nextName });
                  }
                }}
                  className="rounded-xl" placeholder={KINDS[form.kind].placeholder} data-testid="liab-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Outstanding</Label>
                  <CurrencyInput
                    currency={form.currency || currency}
                    value={form.outstanding}
                    onChange={(e) => setForm({ ...form, outstanding: e.target.value })}
                    placeholder="0"
                    data-testid="liab-outstanding-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Currency</Label>
                  <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.code} — {c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label className="flex items-baseline justify-between gap-2">
                    <span>Original loan amount (optional)</span>
                    <span className="text-[10px] text-muted-foreground font-normal">helps calculate &ldquo;paid down&rdquo; %</span>
                  </Label>
                  <CurrencyInput
                    currency={form.currency || currency}
                    value={form.principal_amount}
                    onChange={(e) => setForm({ ...form, principal_amount: e.target.value })}
                    placeholder="0 (skip if same as outstanding)"
                    data-testid="liab-principal-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Monthly payment (optional)</Label>
                  <Input type="number" inputMode="decimal" step="0.01" value={form.emi_amount}
                    onChange={(e) => setForm({ ...form, emi_amount: e.target.value })} className="rounded-xl tabular-nums" />
                </div>
                <div className="space-y-1.5">
                  <Label>Interest rate %</Label>
                  <Input type="number" inputMode="decimal" step="0.01" value={form.interest_rate}
                    onChange={(e) => setForm({ ...form, interest_rate: e.target.value })} className="rounded-xl tabular-nums" />
                </div>
              </div>
              <DialogFooter className="sm:justify-between gap-2">
                {editingId ? (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={async () => {
                      await remove(editingId);
                      setOpen(false);
                      setEditingId(null);
                    }}
                    className="rounded-xl text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                    data-testid="liab-delete-button"
                  >
                    <Trash2 className="h-4 w-4 mr-1.5" /> Delete
                  </Button>
                ) : <span />}
                <div className="flex items-center gap-2">
                  <Button type="button" variant="ghost" onClick={() => { setOpen(false); setEditingId(null); }} className="rounded-xl">Cancel</Button>
                  <Button type="submit" disabled={saving} className="rounded-xl" data-testid="liab-save-button">
                    {saving ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving…</> : (editingId ? 'Save changes' : 'Save')}
                  </Button>
                </div>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {renderTile('all')}
        {ORDER.map((k) => renderTile(k))}
        {totals.credit_cards > 0 && renderTile('credit_cards')}
      </div>

      {/* v3.7.5 Phase 84.29 — Debt Focus ranking. Renders itself null when
          the user has no ranked debts, so this is safe to always mount. */}
      {items.length > 0 && (
        <DebtFocusTable items={items} currency={currency} />
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="rounded-xl flex-wrap h-auto">
          <TabsTrigger value="all" className="rounded-lg" data-testid="liab-tab-all">All <span className="ml-1.5 text-[10px] text-muted-foreground">{items.length}</span></TabsTrigger>
          {ORDER.map((k) => (
            <TabsTrigger key={k} value={k} className="rounded-lg" data-testid={`liab-tab-${k}`}>
              {KINDS[k].label} <span className="ml-1.5 text-[10px] text-muted-foreground">{(grouped[k] || []).length}</span>
            </TabsTrigger>
          ))}
          <TabsTrigger value="credit_cards" className="rounded-lg" data-testid="liab-tab-cards">
            Credit Cards <span className="ml-1.5 text-[10px] text-muted-foreground">{creditCards.length}</span>
          </TabsTrigger>
        </TabsList>
        <TabsContent value={activeTab} className="mt-4 focus-visible:outline-none">
          {loading ? <Skeleton className="h-32 rounded-2xl" /> : activeTab === 'credit_cards' ? (
            creditCards.length === 0 ? (
              <Card className="rounded-2xl border-dashed border-border/70"><CardContent className="p-10 text-center text-sm text-muted-foreground">No credit cards yet. <Link to="/accounts" className="text-primary hover:underline">Add one in Accounts →</Link></CardContent></Card>
            ) : (
              <div className="space-y-2">
                {creditCards.map((c) => {
                  const owed = Number(c.balance || 0);
                  return (
                    <Card key={c.id} className="rounded-2xl border-border/70" data-testid={`liab-card-${c.id.slice(0,8)}`}>
                      <CardContent className="p-4 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-10 w-10 rounded-xl grid place-items-center bg-muted/50 text-foreground"><CreditCard className="h-4 w-4" /></div>
                          <div>
                            <div className="text-sm font-medium">{c.name}</div>
                            <div className="text-xs text-muted-foreground">{c.last4 ? `•••• ${c.last4}` : ''} <Link to={`/accounts/${c.id}/detail`} className="ml-2 text-primary hover:underline">View account</Link></div>
                          </div>
                        </div>
                        <div className={`text-sm font-medium tabular-nums ${owed > 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                          {owed > 0 ? '−' : ''}{formatMoney(Math.abs(owed), c.currency || currency, { compact: true })}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )
          ) : visibleItems.length === 0 ? (
            <Card className="rounded-2xl border-dashed border-border/70">
              <CardContent className="p-10 text-center">
                <FileWarning className="h-6 w-6 mx-auto text-primary mb-2" />
                <div className="font-display text-lg font-semibold">No {activeTab === 'all' ? 'liabilities' : KINDS[activeTab].label.toLowerCase()} yet</div>
                <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">Track your loans and debts so your Net Worth and FI numbers reflect reality.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">{visibleItems.map((i) => renderRow(i))}</div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
